// Ana sayfa widget yerleşimi — Blazor HomePage `UserSettings["WidgetLayout"]` sözleşmesi.
// JSON şeması Blazor'daki `WidgetLayout { Widgets: WidgetDefinition[] }` ile birebir aynıdır,
// böylece Blazor ve React aynı kullanıcı ayarını paylaşır (bilinmeyen alanlar korunur).
export const WIDGET_LAYOUT_SETTING_KEY = 'WidgetLayout';

export type WidgetType =
  | 'Welcome'
  | 'Favorites'
  | 'Dashboards'
  | 'Calendar'
  | 'Notepad'
  | 'ExchangeRate'
  | 'DashboardEmbed'
  | 'Clock'
  | 'AnalogClock'
  | 'RecentActivity';

export interface WidgetDefinition {
  id: string;
  type: WidgetType;
  x: number;
  y: number;
  w: number;
  h: number;
  title: string;
  icon: string;
  config: Record<string, string>;
  /** Blazor'dan gelen tanınmayan alanlar (geri yazarken korunur) */
  extra?: Record<string, unknown>;
}

export interface WidgetCatalogItem {
  type: WidgetType;
  nameKey: string;
  descriptionKey: string;
  defaultW: number;
  defaultH: number;
  /**
   * Boyut sınırları. Her widget'ın altına düşülünce okunamaz hale geldiği bir eşik var:
   * takvim ay ızgarasını yedi sütuna sığdırmak zorunda, saat sayıyı kesmemeli. Üst sınır
   * da anlamlı — bir saat widget'ını yarım ekran yapmak boş alan üretir.
   */
  minW: number;
  minH: number;
  maxW?: number;
  maxH?: number;
  maxCount: number;
}

export const GRID_COLUMNS = 12;
export const ROW_HEIGHT = 56;

/** Blazor WidgetDefaults.GetCatalog() karşılığı (ad/açıklama i18n anahtarı ile). */
export const WIDGET_CATALOG: WidgetCatalogItem[] = [
  { type: 'Welcome', nameKey: 'home.widget.welcome', descriptionKey: 'home.widget.welcomeDesc', defaultW: 8, defaultH: 2, minW: 3, minH: 2, maxH: 3, maxCount: 1 },
  { type: 'Clock', nameKey: 'home.widget.clock', descriptionKey: 'home.widget.clockDesc', defaultW: 4, defaultH: 2, minW: 2, minH: 2, maxH: 4, maxCount: 1 },
  { type: 'AnalogClock', nameKey: 'home.widget.analogClock', descriptionKey: 'home.widget.analogClockDesc', defaultW: 3, defaultH: 4, minW: 2, minH: 3, maxW: 6, maxH: 8, maxCount: 1 },
  { type: 'Favorites', nameKey: 'home.widget.favorites', descriptionKey: 'home.widget.favoritesDesc', defaultW: 4, defaultH: 5, minW: 2, minH: 3, maxCount: 1 },
  { type: 'Dashboards', nameKey: 'home.widget.dashboards', descriptionKey: 'home.widget.dashboardsDesc', defaultW: 4, defaultH: 5, minW: 2, minH: 3, maxCount: 1 },
  // Takvim: yedi sütunluk ay ızgarası + altındaki liste. Üç kolonun altında gün
  // sayıları okunmuyor, altı satırın altında ay ızgarası listeyi eziyor.
  { type: 'Calendar', nameKey: 'home.widget.calendar', descriptionKey: 'home.widget.calendarDesc', defaultW: 8, defaultH: 6, minW: 3, minH: 6, maxH: 12, maxCount: 1 },
  { type: 'Notepad', nameKey: 'home.widget.notepad', descriptionKey: 'home.widget.notepadDesc', defaultW: 4, defaultH: 5, minW: 2, minH: 3, maxCount: 3 },
  { type: 'ExchangeRate', nameKey: 'home.widget.exchange', descriptionKey: 'home.widget.exchangeDesc', defaultW: 4, defaultH: 2, minW: 2, minH: 2, maxH: 6, maxCount: 1 },
  { type: 'RecentActivity', nameKey: 'home.widget.recent', descriptionKey: 'home.widget.recentDesc', defaultW: 4, defaultH: 5, minW: 3, minH: 3, maxCount: 1 },
  { type: 'DashboardEmbed', nameKey: 'home.widget.dashboardEmbed', descriptionKey: 'home.widget.dashboardEmbedDesc', defaultW: 6, defaultH: 5, minW: 3, minH: 4, maxCount: 10 },
];

const CATALOG_BY_TYPE = new Map(WIDGET_CATALOG.map((c) => [c.type, c]));

/** Ekranın ızgarasına verilen boyut sınırları; bilinmeyen tipte güvenli varsayılan. */
export function widgetConstraints(type: WidgetType): { minW: number; minH: number; maxW: number; maxH?: number } {
  const c = CATALOG_BY_TYPE.get(type);
  return {
    minW: c?.minW ?? 2,
    minH: c?.minH ?? 2,
    maxW: Math.min(GRID_COLUMNS, c?.maxW ?? GRID_COLUMNS),
    ...(c?.maxH === undefined ? {} : { maxH: c.maxH }),
  };
}

/**
 * Kayıtlı boyutu sınırların içine çeker.
 *
 * Sınırlar sonradan eklendi: daha önce kaydedilmiş düzenlerde takvim iki satır yüksekliğe
 * indirilmiş olabiliyor ve ızgara okunmuyor. Yükleme sırasında düzeltmek, kullanıcıyı
 * elle uğraştırmaktan iyi.
 */
export function clampWidgetSize(w: WidgetDefinition): WidgetDefinition {
  const c = widgetConstraints(w.type);
  const width = Math.min(c.maxW, Math.max(c.minW, w.w));
  const height = Math.max(c.minH, c.maxH === undefined ? w.h : Math.min(c.maxH, w.h));
  return width === w.w && height === w.h ? w : { ...w, w: width, h: height };
}

/**
 * Varsayılan düzen. Blazor'la aynı ayar anahtarını (`UserSettings["WidgetLayout"]`)
 * paylaşıyor ve şema birebir aynı; yalnızca hangi widget'ların varsayılan geldiği
 * ayrışıyor.
 *
 * Hava durumu kaldırıldı: bir ERP ana sayfasında yer kaplamaktan başka işi yoktu.
 * Boşalan sağ sütun not defterine, alt sıra da takvime verildi — ikisi de günlük işte
 * kullanılan widget'lar. Blazor tarafında hava durumu duruyor; oradan eklenmiş bir
 * kayıt React'te tanınmaz ve düzen React'ten kaydedilirse listeden düşer.
 */
export function defaultWidgets(): WidgetDefinition[] {
  return [
    { id: 'welcome', type: 'Welcome', x: 0, y: 0, w: 8, h: 2, title: '', icon: '', config: {} },
    { id: 'clock', type: 'Clock', x: 8, y: 0, w: 4, h: 2, title: '', icon: '', config: {} },
    { id: 'favorites', type: 'Favorites', x: 0, y: 2, w: 4, h: 5, title: '', icon: '', config: {} },
    { id: 'dashboards', type: 'Dashboards', x: 4, y: 2, w: 4, h: 5, title: '', icon: '', config: {} },
    { id: 'notepad', type: 'Notepad', x: 8, y: 2, w: 4, h: 5, title: '', icon: '', config: {} },
    { id: 'calendar', type: 'Calendar', x: 0, y: 7, w: 8, h: 6, title: '', icon: '', config: {} },
    { id: 'exchange', type: 'ExchangeRate', x: 8, y: 7, w: 4, h: 2, title: '', icon: '', config: {} },
  ];
}

const KNOWN_TYPES = new Set<string>(WIDGET_CATALOG.map((c) => c.type));
const OWN_KEYS = new Set(['Id', 'Type', 'X', 'Y', 'W', 'H', 'Title', 'Icon', 'Config', 'id', 'type', 'x', 'y', 'w', 'h', 'title', 'icon', 'config']);

function num(v: unknown, fallback: number): number {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function pick(raw: Record<string, unknown>, ...keys: string[]): unknown {
  for (const k of keys) if (raw[k] !== undefined) return raw[k];
  return undefined;
}

/** Kullanıcı ayarındaki JSON'u (Blazor PascalCase ya da React camelCase) widget listesine çevirir. */
export function parseWidgetLayout(value: unknown): WidgetDefinition[] {
  const root = typeof value === 'string' ? safeJson(value) : value;
  const list = Array.isArray(root) ? root : Array.isArray((root as { Widgets?: unknown[]; widgets?: unknown[] })?.Widgets ?? (root as { widgets?: unknown[] })?.widgets) ? ((root as { Widgets?: unknown[]; widgets?: unknown[] }).Widgets ?? (root as { widgets?: unknown[] }).widgets)! : null;
  if (!list) return defaultWidgets();
  const out: WidgetDefinition[] = [];
  for (const item of list) {
    if (!item || typeof item !== 'object') continue;
    const raw = item as Record<string, unknown>;
    const type = String(pick(raw, 'Type', 'type') ?? '');
    if (!KNOWN_TYPES.has(type)) continue;
    const extra: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(raw)) if (!OWN_KEYS.has(k)) extra[k] = v;
    const cfgRaw = pick(raw, 'Config', 'config');
    const config: Record<string, string> = {};
    if (cfgRaw && typeof cfgRaw === 'object') for (const [k, v] of Object.entries(cfgRaw as Record<string, unknown>)) config[k] = v == null ? '' : String(v);
    out.push({
      id: String(pick(raw, 'Id', 'id') ?? `${type}-${out.length}`),
      type: type as WidgetType,
      x: Math.max(0, num(pick(raw, 'X', 'x'), 0)),
      y: Math.max(0, num(pick(raw, 'Y', 'y'), 0)),
      w: Math.min(GRID_COLUMNS, Math.max(1, num(pick(raw, 'W', 'w'), 4))),
      h: Math.max(1, num(pick(raw, 'H', 'h'), 3)),
      title: String(pick(raw, 'Title', 'title') ?? ''),
      icon: String(pick(raw, 'Icon', 'icon') ?? ''),
      config,
      ...(Object.keys(extra).length ? { extra } : {}),
    });
  }
  return out.length ? out.map(clampWidgetSize) : defaultWidgets();
}

/** Blazor'ın okuyabileceği biçimde geri yazar (PascalCase + Widgets sarmalayıcısı). */
export function serializeWidgetLayout(widgets: WidgetDefinition[]): string {
  return JSON.stringify({
    Widgets: widgets.map((w) => ({
      ...(w.extra ?? {}),
      Id: w.id,
      Type: w.type,
      X: w.x,
      Y: w.y,
      W: w.w,
      H: w.h,
      Title: w.title,
      Icon: w.icon,
      Config: w.config,
    })),
  });
}

function safeJson(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

/** Katalogdaki üst sınır dolduysa yeni widget eklenemez. */
export function canAddWidget(widgets: WidgetDefinition[], type: WidgetType): boolean {
  const item = WIDGET_CATALOG.find((c) => c.type === type);
  if (!item) return false;
  return widgets.filter((w) => w.type === type).length < item.maxCount;
}

/** Yeni widget'ı ızgaranın en altına ekler. */
export function addWidget(widgets: WidgetDefinition[], type: WidgetType): WidgetDefinition[] {
  const item = WIDGET_CATALOG.find((c) => c.type === type);
  if (!item || !canAddWidget(widgets, type)) return widgets;
  const bottom = widgets.reduce((m, w) => Math.max(m, w.y + w.h), 0);
  const id = `${type.toLowerCase()}-${Math.random().toString(36).slice(2, 8)}`;
  return [...widgets, { id, type, x: 0, y: bottom, w: item.defaultW, h: item.defaultH, title: '', icon: '', config: {} }];
}

export function removeWidget(widgets: WidgetDefinition[], id: string): WidgetDefinition[] {
  return widgets.filter((w) => w.id !== id);
}

/** react-grid-layout düzenini widget listesine uygular (x/y/w/h). */
export function applyGridLayout(widgets: WidgetDefinition[], layout: readonly { i: string; x: number; y: number; w: number; h: number }[]): WidgetDefinition[] {
  const byId = new Map(layout.map((l) => [l.i, l]));
  return widgets.map((w) => {
    const l = byId.get(w.id);
    return l ? { ...w, x: l.x, y: l.y, w: l.w, h: l.h } : w;
  });
}

/** Mobil/tek sütun: widget'lar y sırasına göre alt alta. */
export function stackedOrder(widgets: WidgetDefinition[]): WidgetDefinition[] {
  return widgets.slice().sort((a, b) => a.y - b.y || a.x - b.x);
}
